/**
 * useCreateStorylineModal — shared Create-Storyline modal wiring for every page that
 * hosts the Storyline Menu tree (Overview + Asset detail + Storyline detail). Avoids
 * duplicating the modal state / render / post-create navigation three times.
 *
 * Usage:
 *   const createModal = useCreateStorylineModal({ assets, industries });
 *   <AssetNavTree ... onCreateStoryline={createModal.open} />
 *   {createModal.node}
 *
 * On create success it navigates to the new storyline's detail page (Create-then-fill,
 * mirrors the asset create flow) and calls the optional onCreated so the caller can
 * refresh its own tree skeleton.
 */
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AssetSummary } from '../../../client/IvtHubClient';
import StorylineModal from '../components/StorylineModal';

export interface CreateStorylineModal {
  /** Open the Create modal with `industry` prefilled (called from the tree's `+`). */
  open: (industry: string) => void;
  /** The modal element to render (null when closed). */
  node: ReactNode;
}

export function useCreateStorylineModal({
  assets,
  industries,
  onCreated,
}: {
  assets: AssetSummary[];
  industries: string[];
  /** Optional: called after a successful create (e.g. bump a tree reload key). */
  onCreated?: (storylineId: string) => void;
}): CreateStorylineModal {
  const navigate = useNavigate();
  const [industry, setIndustry] = useState<string | null>(null);

  const node =
    industry === null ? null : (
      <StorylineModal
        mode="create"
        assets={assets}
        industries={industries}
        initial={{ industry }}
        onClose={() => setIndustry(null)}
        onSaved={(storylineId) => {
          setIndustry(null);
          onCreated?.(storylineId);
          navigate(`/ivt/storylines/${storylineId}`);
        }}
      />
    );

  return { open: (ind: string) => setIndustry(ind), node };
}
